import { log } from '../log';
import { getTabsThatCanBeCommunicateWith } from '../utils';
import { isWorkspaceLinkingBridgeAction, publishLinkIntentFromLegacy } from '../workspaceBridge';
import { MATRIX } from './matrix';
import { notUndefined } from '@thinkalpha/common/util/notNullOrUndefined.js';
import { put, takeEvery, all } from 'redux-saga/effects';
import { getUnderlyingSymbolFromInstrument, parseInstrument } from 'src/lib/instrumentUtils';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { updateExecutionsInstrument } from 'src/store/actions/widgets/executions';
import { updateAlphaLensSymbol } from 'src/store/actions/widgets/lens';
import type { WorkspaceLinkingOrderSelectedAction } from 'src/store/actions/widgets/linking';
import { updateMessagesInstrument } from 'src/store/actions/widgets/messages';
import { updateOrderBlotterInstrument } from 'src/store/actions/widgets/orderBlotter';
import type { UserDoubleClickedOrderFromTableAction } from 'src/store/actions/widgets/orders';
import { updateTradeProInstrument } from 'src/store/actions/widgets/tradePro';

function getLinkedOrderInstrument(action: UserDoubleClickedOrderFromTableAction | WorkspaceLinkingOrderSelectedAction) {
    switch (action.type) {
        case 'userDoubleClickedOrderFromTable':
            return action.symbol;
        case 'workspaceLinkingOrderSelected':
            return action.instrument;
        default:
            throw new UnreachableCaseError(action);
    }
}

export function* handleUserSelectedOrder(
    action: UserDoubleClickedOrderFromTableAction | WorkspaceLinkingOrderSelectedAction,
) {
    const instrumentString = getLinkedOrderInstrument(action);
    if (!instrumentString) return;

    if (!isWorkspaceLinkingBridgeAction(action)) {
        yield* publishLinkIntentFromLegacy({
            type: 'instrument-selected',
            fromTabId: action.tabId,
            details: { instrument: instrumentString },
        });
    }

    const widgetsThatShouldReceiveUpdates = yield* getTabsThatCanBeCommunicateWith(action.tabId, MATRIX);
    if (!widgetsThatShouldReceiveUpdates.length) return;

    const instrument = parseInstrument(instrumentString);
    const underlyingSymbol = getUnderlyingSymbolFromInstrument(instrument);

    const widgetUpdates = widgetsThatShouldReceiveUpdates
        .map((x) => {
            const type = x.widget.type;
            switch (type) {
                case 'alpha-lens':
                    return updateAlphaLensSymbol(x.id, underlyingSymbol);
                case 'order-blotter':
                    return updateOrderBlotterInstrument(x.id, instrument, true);
                case 'trade-pro':
                case 'trade-pro-multi':
                    return updateTradeProInstrument(x.id, instrument, true);
                case 'messages':
                    return updateMessagesInstrument(x.id, instrument, true);
                case 'executions':
                    return updateExecutionsInstrument(x.id, instrument, true);
                case 'orders':
                case 'accounts':
                case 'events':
                case 'filings':
                case 'locates':
                case 'lab':
                case 'news':
                case 'manual-review':
                case 'positions':
                case 'scanner':
                case 'screener':
                case 'searchalpha':
                case 'results':
                case 'table':
                case 'watchlist':
                case 'basket-trading':
                case 'alpha-eye':
                case 'notepad':
                case 'platforms':
                case 'symbols':
                case 'traders':
                case 'alerts':
                case 'tradingview-chart':
                case 'backtest':
                    if (TRACE) {
                        log.trace({
                            message: 'order change for widget not handled',
                            widget: x.widget,
                            tabId: x.id,
                            instrument,
                        });
                    }
                    return undefined;
                default:
                    throw new UnreachableCaseError(type);
            }
        })
        .filter(notUndefined);
    if (widgetUpdates.length === 0) return;
    yield all(widgetUpdates.map((x) => put(x)));
}

export function* orderIdLinkingSagas() {
    yield all([
        takeEvery(['userDoubleClickedOrderFromTable', 'workspaceLinkingOrderSelected'], handleUserSelectedOrder),
    ]);
}
